import { db } from './firebase';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  runTransaction,
  increment,
} from 'firebase/firestore';

const GAMES = 'lobbies';

const generateCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({ length: 4 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

const normalizeCode = (code) => code.toUpperCase();

export const lobbyService = {
  createRoom: async (playerId, playerName) => {
    const code = generateCode();
    const ref = doc(db, GAMES, code);
    await setDoc(ref, {
      code,
      hostId: playerId,
      status: 'lobby',
      score: 0,
      wordsGuessed: 0,
      wrongAnswers: 0,
      passesLeft: 3,
      currentWordIndex: 0,
      words: [],
      wordsHistory: [],
      timerRemaining: 60,
      timerRunning: false,
      pausedAt: null,
      playerIds: [playerId],
      players: [{ id: playerId, name: playerName, role: null }],
      doubleUsed: 0,
      doubleActive: false,
      phraseWords: [],
      phraseWordIndex: 0,
      createdAt: serverTimestamp(),
    });
    return code;
  },

  roomExists: async (code) => {
    if (!code) return false;
    try {
      const snap = await getDoc(doc(db, GAMES, normalizeCode(code)));
      return snap.exists() && snap.data().status === 'lobby';
    } catch {
      return false;
    }
  },

  joinRoom: async (code, playerId, playerName) => {
    const ref = doc(db, GAMES, normalizeCode(code));
    // Use a transaction so that concurrent joins don't produce duplicate entries.
    // arrayUnion on objects does deep-equality in Firestore, but role changes can
    // cause false positives — a transaction gives us full control.
    return await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(ref);
      if (!snap.exists()) throw new Error('Stanza non trovata');
      const data = snap.data();

      // Se la stanza non è in 'lobby' ma è vuota (errore di sistema o abbandono), cancellala
      if (data.status !== 'lobby') {
        if (!data.players || data.players.length === 0) {
          transaction.delete(ref);
          throw new Error('Stanza scaduta o vuota. Creane una nuova.');
        }
        throw new Error('Partita già iniziata');
      }

      // Idempotent: already a member → no-op
      if (data.players.find((p) => p.id === playerId)) return data;
      if (data.players.length >= 3) throw new Error('Stanza piena (max 3 giocatori)');

      const updatedPlayers = [...data.players, { id: playerId, name: playerName, role: null }];
      const updatedPlayerIds = [...(data.playerIds || []), playerId];
      transaction.update(ref, { players: updatedPlayers, playerIds: updatedPlayerIds });
      return data;
    });
  },

  assignRole: async (code, playerId, role) => {
    const ref = doc(db, GAMES, normalizeCode(code));
    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(ref);
      if (!snap.exists()) return;
      const players = snap.data().players.map((p) => (p.id === playerId ? { ...p, role } : p));
      transaction.update(ref, { players });
    });
  },

  shuffleRoles: async (code) => {
    const ref = doc(db, GAMES, normalizeCode(code));
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const data = snap.data();
    if (data.players.length !== 3) return;

    const roles = ['giver1', 'giver2', 'guesser'];
    // Fisher-Yates shuffle
    for (let i = roles.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [roles[i], roles[j]] = [roles[j], roles[i]];
    }

    const players = data.players.map((p, i) => ({
      ...p,
      role: roles[i],
    }));

    await updateDoc(ref, { players });
  },

  startCountdown: async (code, words, phraseWords = []) => {
    const ref = doc(db, GAMES, normalizeCode(code));
    await updateDoc(ref, {
      status: 'countdown',
      words,
      wordsHistory: [],
      currentWordIndex: 0,
      score: 0,
      wordsGuessed: 0,
      wrongAnswers: 0,
      passesLeft: 3,
      timerRemaining: 60,
      timerRunning: false,
      pausedAt: null,
      countdownStartedAt: Date.now(),
      wordStartedAtRemaining: 60,
      doubleUsed: 0,
      doubleActive: false,
      phraseWords,
      phraseWordIndex: 0,
    });
  },

  startGame: async (code) => {
    const ref = doc(db, GAMES, normalizeCode(code));
    await updateDoc(ref, {
      status: 'playing',
      timerRunning: true,
      timerStartedAt: Date.now(),
      pausedAt: null,
      wordStartedAtRemaining: 60,
    });
  },

  updateGame: async (code, updates) => {
    const ref = doc(db, GAMES, normalizeCode(code));
    await updateDoc(ref, updates);
  },

  pauseGame: async (code, timerRemaining) => {
    await lobbyService.updateGame(code, {
      status: 'paused',
      timerRunning: false,
      timerRemaining,
      pausedAt: Date.now(),
    });
  },

  resumeGame: async (code, timerRemaining, outcome) => {
    // outcome: { score, wordsGuessed, wrongAnswers, passesLeft, currentWordIndex }
    await lobbyService.updateGame(code, {
      status: 'playing',
      timerRunning: true,
      timerRemaining,
      timerStartedAt: Date.now(),
      pausedAt: null,
      wordStartedAtRemaining: timerRemaining,
      ...outcome,
    });
  },

  waitForGuesser: async (code, timerRemaining, outcome) => {
    const updates = {
      status: 'waiting_ready',
      timerRunning: false,
      timerRemaining,
      pausedAt: Date.now(),
      ...outcome,
    };
    // _incrementDouble is set by handleOutcome when a double word was resolved;
    // we handle it here because doubleActive is already set to false in the outcome
    // before guesserReady is ever called, so the flag would be lost otherwise.
    if (updates._incrementDouble) {
      delete updates._incrementDouble;
      updates.doubleUsed = increment(1);
    }
    await lobbyService.updateGame(code, updates);
  },

  guesserReady: async (code, timerRemaining, extra = {}) => {
    const finalUpdates = {
      status: 'playing',
      timerRunning: true,
      timerRemaining,
      timerStartedAt: Date.now(),
      pausedAt: null,
      wordStartedAtRemaining: timerRemaining,
      ...extra,
    };

    await lobbyService.updateGame(code, finalUpdates);
  },

  endGame: async (code) => {
    await lobbyService.updateGame(code, {
      status: 'finished',
      timerRunning: false,
    });
  },

  toggleDouble: async (code, active) => {
    await lobbyService.updateGame(code, { doubleActive: active });
  },

  returnToLobby: async (code) => {
    const ref = doc(db, GAMES, normalizeCode(code));
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const players = snap.data().players.map((p) => ({ ...p, role: null }));
    await updateDoc(ref, {
      status: 'lobby',
      timerRunning: false,
      timerRemaining: 60,
      pausedAt: null,
      players,
    });
  },

  subscribeToGame: (code, callback) => {
    const ref = doc(db, GAMES, normalizeCode(code));
    return onSnapshot(ref, (snap) => {
      if (snap.exists()) callback(snap.data());
    });
  },

  leaveRoom: async (code, playerId) => {
    const ref = doc(db, GAMES, normalizeCode(code));
    try {
      // Return value from the transaction callback is the authoritative result.
      // Using a closure variable is unsafe because Firestore may retry the callback.
      const wasDeleted = await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(ref);
        if (!snap.exists()) return false; // already gone — treat as deleted

        const data = snap.data();
        const currentPlayers = data.players || [];
        const players = currentPlayers.filter((p) => p.id !== playerId);
        const playerIds = (data.playerIds || []).filter((id) => id !== playerId);

        if (players.length === 0) {
          transaction.delete(ref);
          return true;
        } else {
          transaction.update(ref, {
            players,
            playerIds,
            hostId: players[0]?.id ?? data.hostId,
          });
          return false;
        }
      });
      return wasDeleted;
    } catch (e) {
      console.error("Errore durante l'uscita dalla stanza:", e);
      return false;
    }
  },

  interruptGame: async (code, playerName) => {
    const ref = doc(db, GAMES, normalizeCode(code));
    await updateDoc(ref, {
      status: 'interrupted',
      interruptedBy: playerName,
      interruptedAt: serverTimestamp(),
    });
  },
};
